import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowUp, ArrowDown, Play, RotateCcw } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

interface VerticalPongProps {
  onBack: () => void;
}

const VerticalPong: React.FC<VerticalPongProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playerY, setPlayerY] = useState(150);
  const [cpuY, setCpuY] = useState(150);
  const [ballX, setBallX] = useState(400);
  const [ballY, setBallY] = useState(200);
  const [ballVX, setBallVX] = useState(3);
  const [ballVY, setBallVY] = useState(2);
  const [playerScore, setPlayerScore] = useState(0);
  const [cpuScore, setCpuScore] = useState(0);
  const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());
  
  const gameLoopRef = useRef<number>();
  const containerRef = useRef<HTMLDivElement>(null);

  const PADDLE_HEIGHT = 100;
  const PADDLE_WIDTH = 15;
  const BALL_SIZE = 20;
  const GAME_WIDTH = 800;
  const GAME_HEIGHT = 400;
  const PADDLE_SPEED = 8;

  const moveUp = useCallback(() => {
    setPlayerY(prev => Math.max(0, prev - PADDLE_SPEED * 3));
  }, []);

  const moveDown = useCallback(() => {
    setPlayerY(prev => Math.min(GAME_HEIGHT - PADDLE_HEIGHT, prev + PADDLE_SPEED * 3));
  }, []);

  const startGame = () => {
    setIsPlaying(true);
    setBallX(GAME_WIDTH / 2);
    setBallY(GAME_HEIGHT / 2);
    setBallVX(3);
    setBallVY(2);
  };

  const resetGame = () => {
    setIsPlaying(false);
    setPlayerScore(0);
    setCpuScore(0);
    setPlayerY(150);
    setCpuY(150);
    setBallX(GAME_WIDTH / 2);
    setBallY(GAME_HEIGHT / 2);
  };

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        setPressedKeys(prev => new Set(prev).add(e.key));
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      setPressedKeys(prev => {
        const next = new Set(prev);
        next.delete(e.key);
        return next;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Game loop
  useEffect(() => {
    if (!isPlaying) return;

    const gameLoop = () => {
      // Player movement from keyboard
      if (pressedKeys.has('ArrowUp')) {
        setPlayerY(prev => Math.max(0, prev - PADDLE_SPEED));
      }
      if (pressedKeys.has('ArrowDown')) {
        setPlayerY(prev => Math.min(GAME_HEIGHT - PADDLE_HEIGHT, prev + PADDLE_SPEED));
      }

      // Ball movement
      setBallX(prev => {
        const next = prev + ballVX;
        
        // Player paddle collision
        if (next <= 40 && ballY + BALL_SIZE >= playerY && ballY <= playerY + PADDLE_HEIGHT) {
          setBallVX(Math.abs(ballVX));
          return 40;
        }
        
        // CPU paddle collision
        if (next >= GAME_WIDTH - 55 && ballY + BALL_SIZE >= cpuY && ballY <= cpuY + PADDLE_HEIGHT) {
          setBallVX(-Math.abs(ballVX));
          return GAME_WIDTH - 55;
        }

        // Score
        if (next < 0) {
          setCpuScore(prev => prev + 1);
          setBallX(GAME_WIDTH / 2);
          setBallY(GAME_HEIGHT / 2);
          setBallVX(3);
          return GAME_WIDTH / 2;
        }
        if (next > GAME_WIDTH) {
          setPlayerScore(prev => prev + 1);
          setBallX(GAME_WIDTH / 2);
          setBallY(GAME_HEIGHT / 2);
          setBallVX(-3);
          return GAME_WIDTH / 2;
        }

        return next;
      });

      setBallY(prev => {
        const next = prev + ballVY;
        if (next <= 0 || next >= GAME_HEIGHT - BALL_SIZE) {
          setBallVY(v => -v);
          return Math.max(0, Math.min(GAME_HEIGHT - BALL_SIZE, next));
        }
        return next;
      });

      // CPU AI (slow)
      setCpuY(prev => {
        const paddleCenter = prev + PADDLE_HEIGHT / 2;
        const ballCenter = ballY + BALL_SIZE / 2;
        const diff = ballCenter - paddleCenter;
        const speed = 2; // Slow CPU
        
        if (Math.abs(diff) > 10) {
          return prev + (diff > 0 ? speed : -speed);
        }
        return prev;
      });

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [isPlaying, ballVX, ballVY, pressedKeys, ballY, cpuY, playerY]);

  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col overflow-hidden z-[100]" dir="ltr">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-800/50">
        <Button
          onClick={onBack}
          variant="outline"
          size="lg"
          className="bg-slate-700 border-cyan-400 text-cyan-400 hover:bg-cyan-400/20"
        >
          <ArrowLeft className="mr-2 h-6 w-6" />
          {t("back")}
        </Button>
        <div className="text-3xl font-bold">
          <span className="text-cyan-400">{playerScore}</span>
          <span className="text-slate-400 mx-4">-</span>
          <span className="text-magenta-400" style={{ color: '#d946ef' }}>{cpuScore}</span>
        </div>
      </div>

      {/* Game Area */}
      <div className="flex-1 flex items-center justify-center gap-4">
        {/* Left control button */}
        <div className="flex flex-col gap-4">
          <Button
            onMouseDown={moveUp}
            onTouchStart={moveUp}
            size="lg"
            className={`w-24 h-24 text-4xl ${
              pressedKeys.has('ArrowUp') 
                ? 'bg-cyan-400 text-slate-900' 
                : 'bg-slate-700 text-cyan-400 border-2 border-cyan-400'
            }`}
          >
            <ArrowUp className="h-12 w-12" />
          </Button>
          <Button
            onMouseDown={moveDown}
            onTouchStart={moveDown}
            size="lg"
            className={`w-24 h-24 text-4xl ${
              pressedKeys.has('ArrowDown') 
                ? 'bg-cyan-400 text-slate-900' 
                : 'bg-slate-700 text-cyan-400 border-2 border-cyan-400'
            }`}
          >
            <ArrowDown className="h-12 w-12" />
          </Button>
        </div>

        {/* Game canvas */}
        <div 
          ref={containerRef}
          className="relative bg-slate-800 border-4 border-cyan-400 rounded-lg"
          style={{ width: GAME_WIDTH, height: GAME_HEIGHT }}
        >
          {/* Center line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 border-l-2 border-dashed border-slate-600" />

          {/* Player paddle */}
          <div
            className="absolute bg-cyan-400 rounded shadow-lg"
            style={{
              width: PADDLE_WIDTH,
              height: PADDLE_HEIGHT,
              left: 20,
              top: playerY,
              boxShadow: '0 0 20px #22d3ee',
            }}
          />

          {/* CPU paddle */}
          <div
            className="absolute rounded shadow-lg"
            style={{
              width: PADDLE_WIDTH,
              height: PADDLE_HEIGHT,
              right: 20,
              top: cpuY,
              backgroundColor: '#d946ef',
              boxShadow: '0 0 20px #d946ef',
            }}
          />

          {/* Ball */}
          <div
            className="absolute bg-yellow-400 rounded-full"
            style={{
              width: BALL_SIZE,
              height: BALL_SIZE,
              left: ballX,
              top: ballY,
              boxShadow: '0 0 15px #facc15',
            }}
          />

          {/* Start overlay */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 rounded-lg">
              <div className="text-center">
                <h2 className="text-4xl font-bold text-cyan-400 mb-6">{t("pongGame")}</h2>
                <Button
                  onClick={playerScore > 0 || cpuScore > 0 ? resetGame : startGame}
                  size="lg"
                  className="bg-yellow-400 text-slate-900 hover:bg-yellow-300 text-xl px-8 py-4"
                >
                  {playerScore > 0 || cpuScore > 0 ? <RotateCcw className="mr-2 h-6 w-6" /> : <Play className="mr-2 h-6 w-6" />}
                  {playerScore > 0 || cpuScore > 0 ? t("newGame") : t("startGame")}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Right placeholder for symmetry */}
        <div className="w-24" />
      </div>

      {/* Instructions */}
      <div className="p-4 text-center text-slate-400 text-lg">
        {t("pongInstructions")}
      </div>
    </div>
  );
};

export default VerticalPong;
